//!#system	

<template>
	<div>
		<v-card v-if="isLoading" flat>
			<v-progress-linear :indeterminate="true"></v-progress-linear>
		</v-card>
		<v-card v-if="isLoaded">
			<v-card-title>
				<span class="headline">Rule Parameters</span>
			</v-card-title>
			<v-alert class="ml-4 mr-4" type="error" v-if="!parametersValid">{{parametersValidationMessage}}</v-alert>
			<div class="ml-4 mr-4">
				<v-form
					ref="form"
					v-model="valid"
				>
					<v-autocomplete
						v-model="model.bucket"
						:items="buckets"
						label="Choose the bucket"
						@change="getTriggers(model.bucket)"
					></v-autocomplete>
					<v-select
						ref="triggers"
						v-model="model.triggers"
						:items="triggers"
						label="Choose the trigger"
						:disabled="triggers.length <= 0"
						multiple
						@change="triggersChanged"
						:hint="!bucketCanBeCreated && model.triggers.includes('create') ? 'Note: `create` and changing values is not an option for sub-buckets (like ' + model.bucket + ') that are not directly created.' : ''"
						:persistent-hint="true"
						:class="!bucketCanBeCreated && model.triggers.includes('create') ? 'mb-2' : ''"
					>
						<template v-slot:prepend-item>
							<v-list-item
								class="grey lighten-3"
								@click="closeTriggersList()"
								:disabled="model.triggers.length === 0"
							>
								Done selecting my triggers
							</v-list-item>
						</template>
						<template v-slot:append-item>
							<v-list-item
								class="grey lighten-3"
								@click="closeTriggersList()"
								:disabled="model.triggers.length === 0"
							>
								Done selecting my triggers
							</v-list-item>
						</template>
					</v-select>
					<div v-if="model.triggers.includes('button')">
						Button options:
						<v-layout wrap>
							<v-flex xs3>
								<div style="margin-right: 20px;">
									<v-text-field
										v-model="model.buttonText"
										label="Button text"
										hint="What the button will say"
										:error="!model.buttonText"
									></v-text-field>
								</div>
							</v-flex>
							<v-flex xs3>
								<div style="margin-right: 20px;">
									<v-switch
										v-model="model.buttonRulesMustApply"
										label="Only if rules match"
									></v-switch>
								</div>
							</v-flex>
							<v-flex xs3>
								<div style="margin-right: 20px;">
									<v-switch
										v-model="model.buttonBatchCompatible"
										label="Available on list screen"
									></v-switch>
								</div>
							</v-flex>
							<v-flex xs3>
								<div style="margin-right: 20px;">
									<v-switch
										v-model="model.buttonMustConfirm"
										label="User must confirm"
									></v-switch>
								</div>
							</v-flex>
						</v-layout>
					</div>
					<rules-group v-if="model.bucket && model.triggers.length" v-model="model.group" :bucket="model.bucket" :routeInfo="routeInfo" />
				</v-form>
			</div>
			<div class="ml-4 mr-4">
				<div v-for="(action, index) in model.actions" :key="action.sig">
					<rules-action v-model="model.actions[index]" :bucket="model.bucket" :routeInfo="routeInfo" :matchers="allMatchers" :canRemove="model.actions.length > 1" @remove="removeAction(action.sig)" />
				</div>
				<div v-if="canAddAction">
					<v-tooltip bottom>
						<template v-slot:activator="{ on }">
							<v-btn v-on="on" @click="addAction()" color="secondary" fab x-small dark>
								<v-icon dark x-small>mdi-plus</v-icon>
							</v-btn>
						</template>
						<span>Add an action</span>
					</v-tooltip>
				</div>
			</div>
			<div class="caption mx-4 my-4" v-if="actionHasValueChanger">
				<strong>Note:</strong> 
				The trigger "create" runs before the new object is created, and will change values prior to the object being saved.  The "create" trigger should only be used on top-level buckets that can be directly created.  Otherwise, consider using the "created" trigger to change values after the object has been created. The triggers "button" and "on demand" can also change values, since those triggers have special actions that will update the values if they've changed.
			</div>
			<div class="ml-4 mr-4 mt-4" v-if="model.bucket && model.triggers.length">
				<div v-if="showTheJson">
					<code style="width: 100%;">{{model}}</code>
				</div>
			</div>
			<v-card-actions>
				<v-spacer></v-spacer>
				<v-btn v-if="(model.bucket && model.triggers.length) && showTheJson" text @click="hideCodes()">Hide Config</v-btn>
				<v-btn v-if="(model.bucket && model.triggers.length) && !showTheJson" text @click="revealCodes()">View Config</v-btn>
				<v-btn text @click="closeDialog()">Exit</v-btn>
				<v-btn color="primary" @click="submit()">Save</v-btn>
			</v-card-actions>
		</v-card>
		<v-dialog v-model="activateDialog" max-width="300px">
			<v-card>
				<v-card-title class="headline grey lighten-2" primary-title>
					<div ref="filter-title-element">Activate this rule?</div>
				</v-card-title>
				<v-card-text class="py-6">
					This rule is not yet active, and will not run. 
					Do you want to activate this?
				</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text @click="activateDialog = false">Do not activate</v-btn>
					<v-btn color="primary" text @click="doActivate">
						Activate
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import apiService from '@/apiService';
import RulesGroup from '@/components/pieces/RulesGroup';
import RulesAction from '@/components/pieces/RulesAction';
import series from 'to-series';
import tools from '@/tools.js';

const baseKey = function(start, end) {
	return Math.floor(Math.random() * end) + start;
};

let sampleDefault = null; // TODO: load it from the db

const matchersFlattener = function(group, ar) {
	if (!ar) ar = [];
	if (!group) return ar;
	if (!group.matchers) return ar;
	group.matchers.forEach(matcher => {
		if (matcher.type === 'simple') {
			ar.push(matcher);
		} else if (matcher.type === 'group') {
			matchersFlattener(matcher, ar);
		}
	});
	return ar;
};

const canChangeValueTriggers = ['create', 'update', 'button', 'on demand'];

export default {
	name: 'ruleParameters',
	components: {
		RulesGroup,
		RulesAction
	},
	props: {
		routeInfo: {
			type: Object
		}
	},
	data() {
		return {
			isLoading: false,
			isSubmitting: false,
			buckets: [],
			namespaces: {},
			triggers: [],
			model: sampleDefault || {
				bucket: '',
				triggers: [],
				group: {},
				actions: [{
					sig: 'action-' + baseKey(1500, 2800)
				}]
			},
			isActive: null,
			activateDialog: false,
			valid: false,
			showTheJson: false,
			parametersValid: true,
			parametersValidationMessage: '',
			ruleName: '',
			bucketCanBeCreated: true
		}
	},
	computed: {
		isLoaded() {
			return !this.isLoading;
		},
		objectList() {
			const list = [];
			Object.keys(this.namespaces).forEach(key => {
				this.namespaces[key].forEach(objectName => {
					list.push(key + '/' + objectName);
				});
			});
			return list;
		},
		canAddAction() {
			return !!this.model.bucket;
		},
		allMatchers() {
			const ms = matchersFlattener(this.model.group);
			return ms;
		},
		actionHasValueChanger() {
			let itHasValueChanger = false;
			this.model.actions.forEach(action => {
				if (action.name === 'changeValue') itHasValueChanger = true;
			});
			return itHasValueChanger;
		},
		hasActionsThatWorkWithAnyTrigger() {
			let valid = true;
			this.model.actions.forEach(action => {
				if (action.name !== 'noPermission') valid = false;
			});
			return valid;
		}
	},
	methods: {
		closeTriggersList() {
			this.$refs.triggers.blur();
		},
		revealCodes() {
			this.showTheJson = true;
		},
		hideCodes() {
			this.showTheJson = false;
		},
		closeDialog() {
			this.$emit('close');
			this.$emit('refresh');
		},
		addAction() {
			this.model.actions.push({
				sig: 'action-' + baseKey(1500, 2800)
			});
		},
		removeAction(sig) {
			if (this.model.actions.length <= 1) return; // no deleting the only action!
			for (let i = 0; i < this.model.actions.length; i++) {
				if (sig && this.model.actions[i].sig === sig) {
					this.$delete(this.model.actions, i);
				}
			}
		},
		triggersChanged() {
			if (this.model.triggers.includes('button') && !this.model.buttonText) {
				this.model.buttonText = this.ruleName;
			} else if (!this.model.triggers.includes('button')) {
				this.model.buttonText = undefined;
				this.model.buttonMustConfirm = undefined;
				this.model.buttonRulesMustApply = undefined;
				this.model.buttonBatchCompatible = undefined;
			}
		},
		getNamespaces(done) {
			apiService.action({
				...this.routeInfo,
				action: 'getNamespaces',
				method: 'get'
			}, (err, response, meta) => {
				this.namespaces = response;
				if (done) return done(err);
			});
		},
		setBucketCanBeCreated(done) {
			if (!this.model.bucket) return done();
			this.$store.dispatch('app/fieldInfo', {
				routeInfo: tools.fromBucket(this.model.bucket),
				callback: (err, fieldInfoResponse) => {
					if (err) {
						this.$store.dispatch('app/applyObjectError', {
							show: true,
							text: err 
						});
						return done();
					}
					this.bucketCanBeCreated = !!fieldInfoResponse.createable;
					return done();
				}
			});
		},
		getTriggers(objectPath, done) {
			this.triggers = [];
			let objectPathArray = objectPath.split('/');
			let namespace = objectPathArray[0];
			let objectNamePlural = objectPathArray[1];
			this.setBucketCanBeCreated(() => {
				apiService.rulesTriggers({
					...this.routeInfo,
					pluralName: objectNamePlural,
					namespace: namespace
				}, (err, response, meta) => {
					this.triggers = response.map(trigger => {
						return { text: trigger, value: trigger, disabled: false }
					});
					if (done) return done(err);
				});
			});
		},
		getDbRules(done) {
			apiService.detail(this.routeInfo, (err, response, meta) => {
				if (err) return done(err);
				if (response.ruleData && typeof response.ruleData === 'object' && Object.keys(response.ruleData).length > 0) {
					// Only reset the model if the data from the DB is an actual/valid rule object
					this.model = response.ruleData;
					this.isActive = response.ruleIsActive;
				}
				return done();
			});
		},
		validateTriggers(action) {
			// No used anymore but can be useful in the future
			const selectedTriggers = this.model.triggers;
			let updatedTriggers = [], // I'm going to remove triggers if the user chooses an action that doesn't allow that trigger
				triggersRemoved = [];

			if (this.actionHasValueChanger) {
				// Only `create` and `update` can use the `changeValue` action
				this.triggers.map(trigger => {
					if (canChangeValueTriggers.includes(trigger.value)) {
						trigger.disabled = false;
					} else {
						trigger.disabled = true;
					}
				});
				selectedTriggers.map(trigger => {
					if (canChangeValueTriggers.includes(trigger)) {
						updatedTriggers.push(trigger);
					} else {
						triggersRemoved.push(trigger);
					}
				});
			} else {
				// For all other actions, we need an ID so only `create` is not compatible
				this.triggers.map(trigger => {
					if (trigger.value === 'create') {
						trigger.disabled = true;
					} else {
						trigger.disabled = false;
					}
				});
				selectedTriggers.map(trigger => {
					if (trigger === 'create') {
						triggersRemoved.push(trigger);
					} else {
						updatedTriggers.push(trigger);
					}
				});
			}

			this.model.triggers = updatedTriggers;

			if (triggersRemoved.length) {
				this.$store.dispatch('app/applyObjectError', {
					show: true,
					text: 'Action is incompatible with selected triggers: "' + triggersRemoved.join('", "') + '"'
				});
			}
		},
		validateParameters() {
			let foundInvalid = false;
			this.parametersValid = true;
			if (!this.model.bucket) {
				foundInvalid = true;
				this.parametersValid = false;
				this.parametersValidationMessage = 'You must choose a bucket.';
				return false;
			}

			if (!this.model.triggers.length) {
				foundInvalid = true;
				this.parametersValid = false;
				this.parametersValidationMessage = 'You must choose at least 1 trigger.';
				return false;
			}

			// I think we still have to do more validation on the actions
			if (!this.model.actions.length) {
				foundInvalid = true;
				this.parametersValid = false;
				this.parametersValidationMessage = 'You must choose an action';
				return false;
			}

			if (!this.bucketCanBeCreated && this.model.triggers.includes('create') && this.actionHasValueChanger) {
				//foundInvalid = true;
				//this.parametersValid = false;
				this.parametersValidationMessage = 'The trigger "create" cannot be used with buckets that cannot be directly created. Consider the "created" trigger instead.';
				//return false;
			}

			if (matchersFlattener(this.model.group).length === 0) {
				foundInvalid = true;
				this.parametersValid = false;
				this.parametersValidationMessage = 'You must include at least one matching rule parameter.';
				return false;
			}

			/*if (this.actionHasValueChanger) {
				for (let i = 0; i < this.model.triggers.length; i++) {
					if (!canChangeValueTriggers.includes(this.model.triggers[i])) {
						foundInvalid = true;
						this.parametersValid = false;
						this.parametersValidationMessage = 'The trigger "' + this.model.triggers[i] + '" is not compatible with the chosen action. Please refer to the note at the bottom of this form.';
						break;
					}
				}
			} else if (this.hasActionsThatWorkWithAnyTrigger) {
				// this is good
			} else {
				for (let i = 0; i < this.model.triggers.length; i++) {
					if (this.model.triggers[i] === 'create' || this.model.triggers[i] === 'update') {
						foundInvalid = true;
						this.parametersValid = false;
						this.parametersValidationMessage = 'The "'+this.model.triggers[i]+'" trigger is not compatible with the chosen action. Try using "'+this.model.triggers[i]+'d" instead.';
						break;
					}
				}
			}*/

			if (foundInvalid) this.parametersValid = false;

			return this.parametersValid;
		},
		doActivate() {
			apiService.update({ ...this.routeInfo, body: { ruleIsActive: true } }, (err, response, meta) => {
				if (err) {
					this.$store.dispatch('app/applyObjectError', {
						show: true,
						text: meta.validation ? meta.validation.messages.join('\n') : err 
					});
				} else {
					this.$store.dispatch('app/applyObjectUpdated', {
						show: true,
						text: 'Your rule has been activated.'
					});
					this.activateDialog = false;
				}
				this.$emit('refresh');
			});
		},
		getBuckets(done) {
			this.$store.dispatch('app/buckets', { 
				options: {}, 
				callback: (err, buckets) => {
					this.buckets = buckets;
					return done(err);
				} 
			});
		},
		getInitialData() {
			this.isLoading = true;
			series()
				.first(cb => {
					this.getNamespaces(cb);
				})
				.next(cb => {
					this.getBuckets(cb);
				})
				.next(cb => {
					apiService.detail(this.routeInfo, (err, results) => {
						if (err) return (err);
						this.ruleName = results.ruleName;
						return cb();
					})
				})
				.next(cb => {
					this.getDbRules(cb);
				})
				.next(cb => {
					if (!this.model.bucket) return cb();
					this.getTriggers(this.model.bucket, cb);
				})
				.end(err => {
					this.isLoading = false;
				});
		},
		submit() {
			if (!this.validateParameters()) {

				this.$store.dispatch('app/applyObjectError', {
					show: true,
					text: this.parametersValidationMessage
				});
				return;
			}

			// save the data
			apiService.call({
				method: 'post',
				path: '/' + encodeURIComponent(tools.camelCaseToDash(this.routeInfo.namespace)) + '/' + encodeURIComponent(tools.camelCaseToDash(this.routeInfo.pluralName)) + '/action/set-rules/' + encodeURIComponent(this.routeInfo.id),
				body: {
					ruleData: this.model
				}
			}, (err, response, meta) => {
				if (err) {
					this.$store.dispatch('app/applyObjectError', {
						show: true,
						text: meta.validation ? meta.validation.messages.join('\n') : err 
					});
				} else {
					this.$store.dispatch('app/applyObjectUpdated', {
						show: true,
						text: 'Your rule has been updated.'
					});
					this.$emit('refresh');
					this.isActive = response.ruleIsActive;
					if (!this.isActive) this.activateDialog = true;
				}
			})
		}
	},
	created() {
		this.getInitialData();
	}
}	
</script>